/**
 * PipeTestingAnalytics - Analytics dashboard for the Pipe Testing tracker
 *
 * Sections:
 * 1. Hero summary cards (total systems, completion %, pass/in-progress/pending/failed)
 * 2. Status breakdown with colored bars
 * 3. Breakdown by test type (Hydrostatic, Pneumatic, etc.)
 * 4. Breakdown by fluid code / system
 * 5. Full systems table with status badges
 */

import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { applyLetterhead } from '../../../utils/pdfLetterhead';
import { exportFilename } from '../../../utils/nomenclature';

let _jsPDF = null;
let _autoTable = null;
async function loadPdfLibs() {
  if (!_jsPDF) {
    const [jsPDFMod, autoTableMod] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable'),
    ]);
    _jsPDF = jsPDFMod.default;
    _autoTable = autoTableMod.default;
  }
  return { jsPDF: _jsPDF, autoTable: _autoTable };
}

let _ExcelJS = null;
async function loadExcelLib() {
  if (!_ExcelJS) {
    _ExcelJS = (await import('exceljs')).default;
  }
  return _ExcelJS;
}

const STATUS_COLORS = {
  'Pass / Complete': { bg: '#dcfce7', color: '#166534', bar: '#16a34a' },
  'In Progress': { bg: '#dbeafe', color: '#1e40af', bar: '#2563eb' },
  'Pending': { bg: '#fef3c7', color: '#92400e', bar: '#d97706' },
  'Failed': { bg: '#fee2e2', color: '#991b1b', bar: '#dc2626' },
};
const getStatusColors = (status) => STATUS_COLORS[status] || { bg: '#f3f4f6', color: '#374151', bar: '#9ca3af' };

const PipeTestingAnalytics = ({ isOpen, onClose, projectId }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [reportPdfLoading, setReportPdfLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [activeTab, setActiveTab] = useState('report');
  const [data, setData] = useState({
    summary: {}, statusBreakdown: [], testTypeBreakdown: [], fluidCodeBreakdown: [], systems: [],
  });

  useEffect(() => {
    if (isOpen) fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/pipe-testing/analytics/${projectId}`);
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching Pipe Testing analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExcelExport = async () => {
    try {
      setExcelLoading(true);
      const ExcelJS = await loadExcelLib();
      const wb = new ExcelJS.Workbook();
      wb.creator = 'LGL Tools';
      wb.created = new Date();

      const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
      const HEADER_ARGB = 'FF0F766E';
      const dateStr = new Date().toISOString().slice(0, 10);

      const writeSheet = (name, columns, rows) => {
        const ws = wb.addWorksheet(name);
        const header = ws.getRow(1);
        columns.forEach((col, i) => {
          const cell = header.getCell(i + 1);
          cell.value = col.header;
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_ARGB } };
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
          cell.alignment = { horizontal: i === 0 ? 'left' : 'center', vertical: 'middle', wrapText: true };
        });
        header.commit();
        rows.forEach((rowVals, ri) => {
          const row = ws.getRow(ri + 2);
          rowVals.forEach((v, i) => {
            const cell = row.getCell(i + 1);
            cell.value = v;
            cell.alignment = { horizontal: i === 0 ? 'left' : 'center' };
          });
          row.commit();
        });
        columns.forEach((col, i) => { ws.getColumn(i + 1).width = col.width || 16; });
        return ws;
      };

      const s = data.summary || {};
      writeSheet('Summary', [{ header: 'Metric', width: 24 }, { header: 'Value', width: 18 }], [
        ['Total Systems', num(s.total_records)], ['Fluid Codes', num(s.unique_fluid_codes)],
        ['Pass / Complete', num(s.pass_count)], ['In Progress', num(s.in_progress_count)],
        ['Pending', num(s.pending_count)], ['Failed', num(s.failed_count)],
        ['Completion Rate %', num(s.completion_rate)],
      ]);

      if ((data.statusBreakdown || []).length > 0) {
        writeSheet('Status Breakdown',
          [{ header: 'Status', width: 20 }, { header: 'Count', width: 12 }],
          data.statusBreakdown.map(r => [r.status || '', num(r.count)]));
      }

      if ((data.testTypeBreakdown || []).length > 0) {
        writeSheet('By Test Type',
          [{ header: 'Test Type', width: 20 }, { header: 'Total', width: 10 }, { header: 'Passed', width: 10 }, { header: 'Failed', width: 10 }, { header: 'Completion %', width: 14 }],
          data.testTypeBreakdown.map(r => [r.test_type || '', num(r.total), num(r.passed), num(r.failed), num(r.completion_rate)]));
      }

      if ((data.fluidCodeBreakdown || []).length > 0) {
        writeSheet('By Fluid Code',
          [
            { header: 'Fluid Code', width: 16 }, { header: 'Total', width: 10 },
            { header: 'Passed', width: 10 }, { header: 'In Progress', width: 12 },
            { header: 'Pending', width: 10 }, { header: 'Failed', width: 10 },
            { header: 'Completion %', width: 14 },
          ],
          data.fluidCodeBreakdown.map(r => [
            r.fluid_code || '', num(r.total), num(r.passed), num(r.in_progress),
            num(r.pending), num(r.failed), num(r.completion_rate),
          ]));
      }

      if ((data.systems || []).length > 0) {
        writeSheet('Systems',
          [
            { header: 'Line / System ID', width: 20 }, { header: 'Fluid Code', width: 12 },
            { header: 'System Description', width: 28 }, { header: 'Design Code', width: 14 },
            { header: 'Test Type', width: 16 }, { header: 'Test Medium', width: 18 },
            { header: 'Status', width: 16 }, { header: 'Test Date', width: 14 },
            { header: 'Inspector', width: 18 }, { header: 'Sign-off', width: 16 },
          ],
          data.systems.map(r => [
            r.line_system_id || '', r.fluid_code || '', r.system_description || '',
            r.design_code || '', r.test_type || '', r.test_medium || '',
            r.status || 'Pending', r.test_date ? new Date(r.test_date).toLocaleDateString('en-CA') : '',
            r.inspector || '', r.sign_off || '',
          ]));
      }

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportFilename({ job: projectId, reportSlug: 'Pipe_Testing_Analytics', ext: 'xlsx' });
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating Pipe Testing Excel:', error);
      alert('Failed to generate Excel. Please try again.');
    } finally {
      setExcelLoading(false);
    }
  };

  const handleClientPdf = async () => {
    try {
      setPdfLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/pipe-testing/project/${projectId}`);
      if (!response.data.success) throw new Error('Failed to fetch Pipe Testing data');

      const rows = response.data.data || [];

      const { jsPDF, autoTable } = await loadPdfLibs();
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
      const pageW = doc.internal.pageSize.getWidth();
      const jobNumber = rows.length > 0 ? rows[0].job_number : '';
      const { y: letterheadY } = await applyLetterhead(doc, { instance, accounts, jobNumber, projectId, margin: 14 });

      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('Pipe Testing Log', 14, letterheadY);

      const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${now} | Records: ${rows.length}`, pageW - 14, letterheadY, { align: 'right' });

      const pdfColumns = [
        { header: '#', dataKey: 'idx' },
        { header: 'Line / System', dataKey: 'line_system_id' },
        { header: 'Fluid Code', dataKey: 'fluid_code' },
        { header: 'Description', dataKey: 'system_description' },
        { header: 'Test Type', dataKey: 'test_type' },
        { header: 'Test Medium', dataKey: 'test_medium' },
        { header: 'Status', dataKey: 'status' },
        { header: 'Test Date', dataKey: 'test_date' },
        { header: 'Inspector', dataKey: 'inspector' },
        { header: 'Sign-off', dataKey: 'sign_off' },
      ];

      const pdfRows = rows.map((r, i) => ({
        idx: i + 1,
        line_system_id: r.line_system_id || '-',
        fluid_code: r.fluid_code || '-',
        system_description: r.system_description || '-',
        test_type: r.test_type || '-',
        test_medium: r.test_medium || '-',
        status: r.status || 'Pending',
        test_date: r.test_date ? new Date(r.test_date).toLocaleDateString() : '-',
        inspector: r.inspector || '-',
        sign_off: r.sign_off || '-',
      }));

      autoTable(doc, {
        columns: pdfColumns,
        body: pdfRows,
        startY: letterheadY + 6,
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [15, 118, 110], textColor: 255, fontStyle: 'bold', fontSize: 7 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { cellWidth: 8, halign: 'center' },
        },
        didParseCell: (cellData) => {
          if (cellData.section === 'body' && cellData.column.dataKey === 'status') {
            if (cellData.cell.raw === 'Failed') {
              cellData.cell.styles.textColor = [220, 38, 38];
              cellData.cell.styles.fontStyle = 'bold';
            } else if (cellData.cell.raw === 'Pass / Complete') {
              cellData.cell.styles.textColor = [22, 101, 52];
              cellData.cell.styles.fontStyle = 'bold';
            }
          }
        },
      });

      doc.save(exportFilename({ job: jobNumber, reportSlug: 'Pipe_Testing_Log', ext: 'pdf' }));
    } catch (error) {
      console.error('Error generating client PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const buildReportPdf = async () => {
    const { jsPDF, autoTable } = await loadPdfLibs();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 14;

    const { y: letterheadY, resolved } = await applyLetterhead(doc, { instance, accounts, projectId, margin });
    const jobNumber = resolved?.context?.job_number || '';

    const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text('Pipe Testing Analytics Report', margin, letterheadY);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`Generated: ${now}`, pageW - margin, letterheadY, { align: 'right' });

    const s = data.summary || {};
    const statusBreakdown = data.statusBreakdown || [];
    const testTypeBreakdown = data.testTypeBreakdown || [];
    const fluidCodeBreakdown = data.fluidCodeBreakdown || [];
    const systems = data.systems || [];
    const total = parseInt(s.total_records) || 0;
    const completionRate = parseFloat(s.completion_rate) || 0;

    const ensureSpace = (y, needed) => {
      if (y + needed > pageH - 12) {
        doc.addPage();
        return 16;
      }
      return y;
    };

    const drawBar = (y, title, rgb) => {
      y = ensureSpace(y, 12);
      doc.setFillColor(...rgb);
      doc.roundedRect(margin, y, pageW - margin * 2, 8, 1.5, 1.5, 'F');
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text(String(title).toUpperCase(), pageW / 2, y + 5.5, { align: 'center' });
      return y + 8;
    };

    const tableOpts = {
      styles: { fontSize: 7.5, cellPadding: 1.6, halign: 'center' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      theme: 'grid',
    };

    let y = letterheadY + 6;

    const heroW = pageW - margin * 2;
    const heroH = 28;
    y = ensureSpace(y, heroH + 22);
    doc.setFillColor(15, 118, 110);
    doc.roundedRect(margin, y, heroW, heroH, 3, 3, 'F');
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('OVERALL COMPLETION', margin + 6, y + 8);
    doc.setFontSize(20);
    doc.text(`${completionRate.toFixed(1)}%`, margin + 6, y + 20);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text(`${s.pass_count || 0} of ${total} systems tested and passed`, margin + 70, y + 19);

    y += heroH + 6;
    const cards = [
      { label: 'Total Systems', value: total, color: [15, 118, 110] },
      { label: 'Fluid Codes', value: s.unique_fluid_codes || 0, color: [8, 145, 178] },
      { label: 'Pass / Complete', value: s.pass_count || 0, color: [22, 163, 74] },
      { label: 'In Progress', value: s.in_progress_count || 0, color: [37, 99, 235] },
      { label: 'Pending', value: s.pending_count || 0, color: [217, 119, 6] },
      { label: 'Failed', value: s.failed_count || 0, color: [220, 38, 38] },
    ];
    const gap = 4;
    const cardW = (pageW - margin * 2 - gap * 5) / 6;
    const cardH = 16;
    cards.forEach((c, i) => {
      const x = margin + i * (cardW + gap);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, y, cardW, cardH, 2, 2, 'F');
      doc.setFillColor(...c.color);
      doc.rect(x, y, 1.5, cardH, 'F');
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...c.color);
      doc.text(String(c.value), x + 4, y + 7);
      doc.setFontSize(6);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(c.label, x + 4, y + 13);
    });
    y += cardH + 8;

    if (statusBreakdown.length > 0) {
      y = drawBar(y, 'Status Breakdown', [15, 118, 110]);
      autoTable(doc, {
        ...tableOpts,
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Status', 'Count', 'Share']],
        body: statusBreakdown.map((r) => {
          const count = parseInt(r.count) || 0;
          return [r.status || '-', count, total > 0 ? `${((count / total) * 100).toFixed(0)}%` : '0%'];
        }),
        headStyles: { fillColor: [15, 118, 110], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    if (testTypeBreakdown.length > 0) {
      y = drawBar(y, 'Breakdown by Test Type', [8, 145, 178]);
      autoTable(doc, {
        ...tableOpts,
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Test Type', 'Total', 'Passed', 'Failed', 'Completion']],
        body: testTypeBreakdown.map((r) => [
          r.test_type || '-',
          parseInt(r.total) || 0,
          parseInt(r.passed) || 0,
          parseInt(r.failed) || 0,
          `${(parseFloat(r.completion_rate) || 0).toFixed(0)}%`,
        ]),
        headStyles: { fillColor: [8, 145, 178], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
        didParseCell: (cellData) => {
          if (cellData.section === 'body' && cellData.column.index === 3 && parseInt(cellData.cell.raw) > 0) {
            cellData.cell.styles.textColor = [220, 38, 38];
            cellData.cell.styles.fontStyle = 'bold';
          }
        },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    if (fluidCodeBreakdown.length > 0) {
      y = drawBar(y, 'Breakdown by Fluid Code / System', [51, 65, 85]);
      autoTable(doc, {
        ...tableOpts,
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Fluid Code', 'Total', 'Passed', 'In Progress', 'Pending', 'Failed', 'Completion']],
        body: fluidCodeBreakdown.map((r) => [
          r.fluid_code || '-',
          parseInt(r.total) || 0,
          parseInt(r.passed) || 0,
          parseInt(r.in_progress) || 0,
          parseInt(r.pending) || 0,
          parseInt(r.failed) || 0,
          `${(parseFloat(r.completion_rate) || 0).toFixed(0)}%`,
        ]),
        headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
        didParseCell: (cellData) => {
          if (cellData.section === 'body' && cellData.column.index === 5 && parseInt(cellData.cell.raw) > 0) {
            cellData.cell.styles.textColor = [220, 38, 38];
            cellData.cell.styles.fontStyle = 'bold';
          }
        },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    if (systems.length > 0) {
      y = drawBar(y, `All Systems (${systems.length})`, [30, 58, 95]);
      autoTable(doc, {
        ...tableOpts,
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Line / System ID', 'Fluid Code', 'Description', 'Test Type', 'Status', 'Test Date', 'Inspector', 'Sign-off']],
        body: systems.map((r) => [
          r.line_system_id || '-',
          r.fluid_code || '-',
          r.system_description || '-',
          r.test_type || '-',
          r.status || 'Pending',
          r.test_date ? new Date(r.test_date).toLocaleDateString('en-CA') : '-',
          r.inspector || '-',
          r.sign_off || '-',
        ]),
        headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: 'bold', fontSize: 7 },
        styles: { fontSize: 7, cellPadding: 1.4, halign: 'center' },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' }, 2: { halign: 'left' } },
        didParseCell: (cellData) => {
          if (cellData.section === 'body' && cellData.column.index === 4) {
            const raw = String(cellData.cell.raw || '');
            if (raw === 'Failed') {
              cellData.cell.styles.textColor = [220, 38, 38];
              cellData.cell.styles.fontStyle = 'bold';
            } else if (raw === 'Pass / Complete') {
              cellData.cell.styles.textColor = [22, 101, 52];
              cellData.cell.styles.fontStyle = 'bold';
            }
          }
        },
      });
    }

    const filename = exportFilename({ job: jobNumber || projectId, reportSlug: 'Pipe_Testing_Analytics', ext: 'pdf' });
    return { doc, filename, jobNumber };
  };

  const handleReportPdf = async () => {
    try {
      setReportPdfLoading(true);
      const { doc, filename } = await buildReportPdf();
      doc.save(filename);
    } catch (error) {
      console.error('Error generating Pipe Testing report PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setReportPdfLoading(false);
    }
  };

  const handleSendEmail = async () => {
    const recipients = emailAddresses.split(/[,;\s]+/).filter((e) => e.includes('@'));
    if (recipients.length === 0) {
      setEmailResult({ type: 'error', message: 'Enter at least one valid email address.' });
      return;
    }
    setEmailSending(true);
    setEmailResult(null);
    try {
      const { doc, filename } = await buildReportPdf();
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/email/send', {
        to: recipients,
        subject: 'Pipe Testing Analytics Report',
        htmlBody: `<p>Please find attached the Pipe Testing analytics report (completion, status, and system summary).</p><p>Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.</p>`,
        attachments: [{ filename, fileblob: pdfBase64 }],
        feature: 'analytics_report',
      });
      if (response.data?.success) {
        setEmailResult({ type: 'success', message: `Sent to ${recipients.length} recipient(s).` });
        setTimeout(() => { setShowEmailModal(false); setEmailResult(null); setEmailAddresses(''); }, 2000);
      } else {
        setEmailResult({ type: 'error', message: response.data?.message || response.data?.error || 'Failed to send email.' });
      }
    } catch (error) {
      setEmailResult({ type: 'error', message: error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to send email.' });
    } finally {
      setEmailSending(false);
    }
  };

  if (!isOpen) return null;

  const { summary, statusBreakdown, testTypeBreakdown, fluidCodeBreakdown, systems } = data;
  const s = summary || {};
  const total = parseInt(s.total_records) || 0;
  const completionRate = parseFloat(s.completion_rate) || 0;

  const renderHeroCards = () => {
    const cards = [
      { label: 'Total Systems', value: total, color: '#0f766e' },
      { label: 'Fluid Codes', value: s.unique_fluid_codes || 0, color: '#0891b2' },
      { label: 'Pass / Complete', value: s.pass_count || 0, color: '#16a34a' },
      { label: 'In Progress', value: s.in_progress_count || 0, color: '#2563eb' },
      { label: 'Pending', value: s.pending_count || 0, color: '#d97706' },
      { label: 'Failed', value: s.failed_count || 0, color: '#dc2626' },
    ];

    return (
      <div style={{ marginBottom: '24px' }}>
        {/* Big completion hero */}
        <div style={{
          background: 'linear-gradient(135deg, #0f766e 0%, #0891b2 100%)', borderRadius: '12px',
          padding: '24px', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: '16px', flexWrap: 'wrap', gap: '16px',
        }}>
          <div>
            <div style={{ fontSize: '13px', opacity: 0.85, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Overall Completion
            </div>
            <div style={{ fontSize: '48px', fontWeight: '800', lineHeight: 1.1 }}>{completionRate.toFixed(1)}%</div>
            <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '4px' }}>
              {s.pass_count || 0} of {total} systems tested and passed
            </div>
          </div>
          <div style={{ width: '160px', height: '160px', position: 'relative' }}>
            <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
              <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="3" />
              <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none" stroke="#fff" strokeWidth="3"
                strokeDasharray={`${Math.min(100, completionRate)}, 100`} strokeLinecap="round" />
            </svg>
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              fontSize: '24px', fontWeight: '800',
            }}>
              {completionRate.toFixed(0)}%
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px' }}>
          {cards.map((card, i) => (
            <div key={i} style={{
              padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb',
              backgroundColor: '#fff', textAlign: 'center',
            }}>
              <div style={{ fontSize: '22px', fontWeight: '700', color: card.color }}>{card.value}</div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', fontWeight: '500' }}>{card.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderStatusBreakdown = () => {
    if (!statusBreakdown || statusBreakdown.length === 0) return null;
    const maxCount = Math.max(...statusBreakdown.map(r => parseInt(r.count) || 0), 1);

    return (
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          backgroundColor: '#0f766e', color: '#fff', padding: '10px 16px', borderRadius: '8px 8px 0 0',
          textAlign: 'center', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase',
        }}>
          Status Breakdown
        </div>
        <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', padding: '16px', backgroundColor: '#fafafa' }}>
          {statusBreakdown.map((row, idx) => {
            const count = parseInt(row.count) || 0;
            const colors = getStatusColors(row.status);
            const width = (count / maxCount) * 100;
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', gap: '12px' }}>
                <div style={{ width: '140px', fontSize: '13px', fontWeight: '600', color: colors.color, textAlign: 'right' }}>
                  {row.status}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ width: '100%', backgroundColor: '#e5e7eb', borderRadius: '4px', height: '22px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${width}%`, height: '100%', backgroundColor: colors.bar,
                      borderRadius: '4px', display: 'flex', alignItems: 'center', paddingLeft: '8px',
                      color: '#fff', fontSize: '12px', fontWeight: '700', minWidth: '28px',
                    }}>
                      {count}
                    </div>
                  </div>
                </div>
                <div style={{ width: '60px', fontSize: '12px', color: '#6b7280', textAlign: 'right' }}>
                  {total > 0 ? `${((count / total) * 100).toFixed(0)}%` : '0%'}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTestTypeBreakdown = () => {
    if (!testTypeBreakdown || testTypeBreakdown.length === 0) return null;
    return (
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          backgroundColor: '#0891b2', color: '#fff', padding: '10px 16px', borderRadius: '8px 8px 0 0',
          textAlign: 'center', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase',
        }}>
          Breakdown by Test Type
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', border: '1px solid #e5e7eb' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc' }}>
              <th style={thStyle}>Test Type</th>
              <th style={thStyle}>Total</th>
              <th style={thStyle}>Passed</th>
              <th style={thStyle}>Failed</th>
              <th style={{ ...thStyle, minWidth: '160px' }}>Completion</th>
            </tr>
          </thead>
          <tbody>
            {testTypeBreakdown.map((row, idx) => {
              const completion = parseFloat(row.completion_rate) || 0;
              const barColor = completion >= 100 ? '#16a34a' : completion >= 50 ? '#2563eb' : completion > 0 ? '#d97706' : '#94a3b8';
              return (
                <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  <td style={{ ...tdStyle, fontWeight: '600' }}>{row.test_type}</td>
                  <td style={tdStyleCenter}>{parseInt(row.total) || 0}</td>
                  <td style={tdStyleCenter}>{parseInt(row.passed) || 0}</td>
                  <td style={{ ...tdStyleCenter, color: parseInt(row.failed) > 0 ? '#dc2626' : '#6b7280' }}>
                    {parseInt(row.failed) || 0}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ flex: 1, backgroundColor: '#e5e7eb', borderRadius: '4px', height: '10px', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(100, completion)}%`, height: '100%', backgroundColor: barColor, borderRadius: '4px' }} />
                      </div>
                      <span style={{ fontWeight: '700', color: barColor, fontSize: '12px', minWidth: '42px' }}>{completion.toFixed(0)}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderFluidCodeBreakdown = () => {
    if (!fluidCodeBreakdown || fluidCodeBreakdown.length === 0) return null;
    return (
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          backgroundColor: '#334155', color: '#fff', padding: '10px 16px', borderRadius: '8px 8px 0 0',
          textAlign: 'center', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase',
        }}>
          Breakdown by Fluid Code / System
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', border: '1px solid #e5e7eb' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc' }}>
              <th style={thStyle}>Fluid Code</th>
              <th style={thStyle}>Total</th>
              <th style={thStyle}>Passed</th>
              <th style={thStyle}>In Progress</th>
              <th style={thStyle}>Pending</th>
              <th style={thStyle}>Failed</th>
              <th style={{ ...thStyle, minWidth: '150px' }}>Completion</th>
            </tr>
          </thead>
          <tbody>
            {fluidCodeBreakdown.map((row, idx) => {
              const completion = parseFloat(row.completion_rate) || 0;
              const barColor = completion >= 100 ? '#16a34a' : completion >= 50 ? '#2563eb' : completion > 0 ? '#d97706' : '#94a3b8';
              return (
                <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  <td style={{ ...tdStyle, fontWeight: '600' }}>{row.fluid_code}</td>
                  <td style={tdStyleCenter}>{parseInt(row.total) || 0}</td>
                  <td style={{ ...tdStyleCenter, color: '#16a34a', fontWeight: '600' }}>{parseInt(row.passed) || 0}</td>
                  <td style={{ ...tdStyleCenter, color: '#2563eb' }}>{parseInt(row.in_progress) || 0}</td>
                  <td style={{ ...tdStyleCenter, color: '#d97706' }}>{parseInt(row.pending) || 0}</td>
                  <td style={{ ...tdStyleCenter, color: parseInt(row.failed) > 0 ? '#dc2626' : '#6b7280' }}>{parseInt(row.failed) || 0}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ flex: 1, backgroundColor: '#e5e7eb', borderRadius: '4px', height: '10px', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(100, completion)}%`, height: '100%', backgroundColor: barColor, borderRadius: '4px' }} />
                      </div>
                      <span style={{ fontWeight: '700', color: barColor, fontSize: '12px', minWidth: '42px' }}>{completion.toFixed(0)}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSystemsTable = () => {
    if (!systems || systems.length === 0) return null;
    return (
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          backgroundColor: '#1e3a5f', color: '#fff', padding: '10px 16px', borderRadius: '8px 8px 0 0',
          textAlign: 'center', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase',
        }}>
          All Systems ({systems.length})
        </div>
        <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e5e7eb' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc', position: 'sticky', top: 0 }}>
                <th style={thStyle}>Line / System ID</th>
                <th style={thStyle}>Fluid Code</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Test Type</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Test Date</th>
                <th style={thStyle}>Inspector</th>
                <th style={thStyle}>Sign-off</th>
              </tr>
            </thead>
            <tbody>
              {systems.map((row, idx) => {
                const colors = getStatusColors(row.status || 'Pending');
                return (
                  <tr key={row.id || idx} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>{row.line_system_id || '-'}</td>
                    <td style={tdStyleCenter}>{row.fluid_code || '-'}</td>
                    <td style={tdStyle}>{row.system_description || '-'}</td>
                    <td style={tdStyleCenter}>{row.test_type || '-'}</td>
                    <td style={tdStyleCenter}>
                      <span style={{
                        padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700',
                        backgroundColor: colors.bg, color: colors.color,
                      }}>
                        {row.status || 'Pending'}
                      </span>
                    </td>
                    <td style={tdStyleCenter}>{row.test_date ? new Date(row.test_date).toLocaleDateString('en-CA') : '-'}</td>
                    <td style={tdStyle}>{row.inspector || '-'}</td>
                    <td style={tdStyle}>{row.sign_off || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', width: '1100px', maxHeight: '90vh',
        overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, #0f766e 0%, #0891b2 100%)', color: 'white',
          flexShrink: 0,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>Pipe Testing Analytics Dashboard</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.9 }}>
              Pressure test completion, status, and compliance tracking
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => { setEmailResult(null); setShowEmailModal(true); }}
              disabled={loading || reportPdfLoading || emailSending}
              title="Email the Report page as a PDF"
              style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                padding: '8px 14px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              📧 Email
            </button>
            <button
              onClick={handleReportPdf}
              disabled={reportPdfLoading || loading}
              title="Download the Report page as a PDF (matches the on-screen layout)"
              style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                fontSize: '13px', fontWeight: '600', cursor: reportPdfLoading ? 'wait' : 'pointer',
                padding: '8px 14px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              {reportPdfLoading ? 'Generating...' : '📥 PDF'}
            </button>
            <button
              onClick={handleExcelExport}
              disabled={excelLoading || loading}
              title="Download analytics report as Excel"
              style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                fontSize: '13px', fontWeight: '600', cursor: excelLoading ? 'wait' : 'pointer',
                padding: '8px 14px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              {excelLoading ? 'Generating...' : '📊 Excel'}
            </button>
            <button onClick={onClose} style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
              fontSize: '20px', cursor: 'pointer', width: '32px', height: '32px',
              borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>&times;</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f8fafc', flexShrink: 0 }}>
          {[
            { id: 'report', label: 'Report' },
            { id: 'client', label: 'Client' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'client') handleClientPdf();
              }}
              style={{
                padding: '12px 24px', border: 'none', cursor: 'pointer',
                fontSize: '13px', fontWeight: '600',
                backgroundColor: activeTab === tab.id ? '#fff' : 'transparent',
                color: activeTab === tab.id ? '#0f766e' : '#6b7280',
                borderBottom: activeTab === tab.id ? '2px solid #0f766e' : '2px solid transparent',
              }}
            >
              {tab.id === 'client' && pdfLoading ? 'Generating PDF...' : tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {activeTab === 'client' ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
              {pdfLoading ? (
                <>
                  <div style={{ fontSize: '16px', marginBottom: '8px' }}>Generating PDF...</div>
                  <div style={{ fontSize: '13px' }}>Preparing the Pipe Testing log for download</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '16px', marginBottom: '12px' }}>Client PDF Export</div>
                  <div style={{ fontSize: '13px', marginBottom: '16px' }}>Click below to download the Pipe Testing log as a PDF document</div>
                  <button
                    onClick={handleClientPdf}
                    style={{
                      padding: '10px 24px', backgroundColor: '#0f766e', color: '#fff',
                      border: 'none', borderRadius: '6px', cursor: 'pointer',
                      fontSize: '14px', fontWeight: '600',
                    }}
                  >
                    Download PDF
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
                  <div style={{ fontSize: '16px', marginBottom: '8px' }}>Loading analytics...</div>
                  <div style={{ fontSize: '13px' }}>Calculating statistics across all pipe testing records</div>
                </div>
              ) : total === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
                  <div style={{ fontSize: '16px', marginBottom: '8px' }}>No Pipe Testing data available yet</div>
                  <div style={{ fontSize: '13px' }}>Import systems from fluid codes or add records to see analytics</div>
                </div>
              ) : (
                <>
                  {renderHeroCards()}
                  {renderStatusBreakdown()}
                  {renderTestTypeBreakdown()}
                  {renderFluidCodeBreakdown()}
                  {renderSystemsTable()}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {showEmailModal && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget && !emailSending) setShowEmailModal(false); }}
        >
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', width: '460px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem', color: '#0f766e' }}>📧 Email Testing Report</h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 0.75rem' }}>
              Enter email addresses separated by commas. A PDF of the Report page (completion and system summary) will be attached.
            </p>
            <textarea
              value={emailAddresses}
              onChange={(e) => setEmailAddresses(e.target.value)}
              placeholder="email1@example.com, email2@example.com"
              style={{ width: '100%', minHeight: '80px', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
            {emailResult && (
              <div style={{
                marginTop: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.85rem',
                backgroundColor: emailResult.type === 'success' ? '#ecfdf5' : '#fef2f2',
                color: emailResult.type === 'success' ? '#059669' : '#dc2626',
              }}>
                {emailResult.message}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                onClick={() => { if (!emailSending) { setShowEmailModal(false); setEmailResult(null); } }}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={emailSending || !emailAddresses.trim()}
                style={{ padding: '0.5rem 1rem', backgroundColor: emailSending ? '#9ca3af' : '#0f766e', color: 'white', border: 'none', borderRadius: '8px', cursor: emailSending ? 'wait' : 'pointer', fontSize: '0.85rem', fontWeight: '500' }}
              >
                {emailSending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb',
  fontSize: '12px', fontWeight: '700', color: '#374151', textTransform: 'uppercase',
};

const tdStyle = {
  padding: '8px 12px',
};

const tdStyleCenter = {
  padding: '8px 12px', textAlign: 'center',
};

export default PipeTestingAnalytics;
